import React, { useCallback, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import Select from 'react-select';
import axios from 'axios';
import { PropertyForm, PropertyMatch, Result } from '../types';
import Municipality from './Municipality';
import PropertySection from './PropertySection';
import produce from 'immer';
import Button from '../../../components/Button';
import qs from 'qs';
import PropertyResults from '../PropertyResults';
import debounce from 'lodash.debounce';
import { Loader } from 'semantic-ui-react';
import { useHistory, useLocation } from 'react-router-dom';

const customStyles = {
  container: (provided: any, state: any) => ({
    ...provided,
    marginTop: 8,
    width: 630,
  }),
  control: (provided: any, state: any) => ({
    ...provided,
    border: '1px solid #e5e5ea',
    height: 40,
  }),
};
const Header = styled.div`
  display: grid;
  grid-template-columns: 18% 16% 10% 8% 8% 8% 25%;
  grid-column-gap: 1%;
  color: #acacb1;
  font-weight: bold;
  border: 1px solid #e5e5ea;
  border-radius: 6px 6px 0px 0px;
  padding: 16px;
  margin: 16px 16px 0px 16px;
  background-color: #f9f9fa;
`;
const AddressInfo = () => {
  const createProperty = () => {
    const property: PropertyForm = {
      id: '',
      address: '',
      unit: '',
      section: '',
      block: '',
      lot: '',
      owners: null,
      qualifier: '',
      selectedCity: {
        county: '',
        municipality: '',
        municipalityId: null,
        municipalityType: '',
        stateCode: '',
        stateName: '',
        subMunicipality: '',
        subMunicipalityId: null,
        subMunicipalityType: '',
        swisCode: '',
        rpadSwisCode: '',
        countyId: null,
        id: null,
        municipalityTypeId: null,
        subMunicipalityTypeId: null,
        hamletId: null,
        hamletName: '',
      },
    };

    return property;
  };
  const [property, setProperty] = useState<PropertyForm>(createProperty());
  const [matches, setMatches] = React.useState<PropertyMatch>();
  const [state, setState] = useState({ value: null, label: '' });
  const [isLoading, setIsLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [typeAheadMatches, setTypeAheadMatches] = useState<PropertyMatch[]>([]);
  const [resultsLoading, setResultsLoading] = useState(false);
  const [addressResultClicked, setAddressResultClicked] = useState(false);
  const history = useHistory();
  const location = useLocation();
  const isNj = state.value == 3;
  const locationList = [
    { value: 1, label: 'NY State' },
    { value: 2, label: 'NYC' },
    { value: 3, label: 'NJ' },
  ];
  const changePropertyInfo = (label: keyof Partial<PropertyForm>, value: any) => {
    const newProperty = produce(property, draft => {
      (draft[label] as keyof PropertyForm) = value;
    });
    if (addressResultClicked) {
      setAddressResultClicked(false);
    }
    setProperty(newProperty);
  };
  const boroMapper = {
    Manhattan: '1',
    Bronx: '2',
    Brooklyn: '3',
    Queens: '4',
    'Staten Island': '5',
  };
  const getNumberOfBorough = boroughName => {
    return boroMapper[boroughName];
  };
  const findMatches = useCallback(
    debounce(
      async (
        e: any,
        property: PropertyForm,
        pageSize: number,
        setFunction: React.Dispatch<React.SetStateAction<PropertyMatch[]>>
      ) => {
        setIsLoading(true);
        e.preventDefault();
        setSearchText(e.target.value);
        await callRpadValidation(e.target.value, property, pageSize, setFunction, false, state);
        setIsLoading(false);
      },
      600
    ),
    [state]
  );

  const callRpadValidation = async (
    value: string,
    property: PropertyForm,
    returnCount: number = 30,
    setFunction: any = setMatches,
    isPropertyResults: boolean,
    state: { value: number; label: string }
  ) => {
    isPropertyResults && setResultsLoading(true);
    const queryOptions = {
      address: value || property.address,
      section: property.section,
      block: property.block || undefined,
      lot: property.lot || undefined,
      qualifier: property.qualifier,
      owner: property.owners,
      swiss: property.selectedCity?.swisCode || undefined,
      borough: getNumberOfBorough(property.selectedCity?.municipality),
      returnCount,
    };
    const stringifiedParams = qs.stringify(queryOptions);
    const baseUrl = `/api/clientproperty/validateRpad/${
      !state?.value ? 'default' : state.value == 3 ? 'NJ' : state.value == 2 ? 'NYC' : 'NYS'
    }`;
    const { data } = await axios.get(`${baseUrl}?${stringifiedParams}`);
    setFunction(data);
    isPropertyResults && setResultsLoading(false);
  };

  const onClear = () => {
    setState({ value: 0, label: '' });
    setAddressResultClicked(false);
    setMatches(null);
    setProperty({
      id: '',
      address: '',
      unit: '',
      section: '',
      block: '',
      lot: '',
      owners: null,
      qualifier: '',
      selectedCity: {
        county: '',
        municipality: '',
        municipalityId: null,
        municipalityType: '',
        stateCode: '',
        stateName: '',
        subMunicipality: '',
        subMunicipalityId: null,
        subMunicipalityType: '',
        swisCode: '',
        rpadSwisCode: '',
        countyId: null,
        id: null,
        municipalityTypeId: null,
        subMunicipalityTypeId: null,
        hamletId: null,
        hamletName: '',
      },
    });
    history.replace('/address-lookup');
  };

  const onSearch = () => {
    const params = qs.stringify({ ...property, state });
    history.replace(`/address-lookup?${params}`);
  };

  useEffect(() => {
    const property: any = qs.parse(location.search.slice(1));
    if (Object.keys(property).length > 0) {
      callRpadValidation('', property, 30, setMatches, true, property.state);
      setProperty({ ...property, state: undefined });
      setState(property.state || { value: null, label: '' });
    } else {
      setState({ value: 0, label: '' });
      setAddressResultClicked(false);
      setMatches(null);
      setProperty({
        id: '',
        address: '',
        unit: '',
        section: '',
        block: '',
        lot: '',
        owners: null,
        qualifier: '',
        selectedCity: {
          county: '',
          municipality: '',
          municipalityId: null,
          municipalityType: '',
          stateCode: '',
          stateName: '',
          subMunicipality: '',
          subMunicipalityId: null,
          subMunicipalityType: '',
          swisCode: '',
          rpadSwisCode: '',
          countyId: null,
          id: null,
          municipalityTypeId: null,
          subMunicipalityTypeId: null,
          hamletId: null,
          hamletName: '',
        },
      });
    }
  }, [location.search]);

  return (
    <>
      <div style={{ maxWidth: 630, margin: '0 auto', paddingBottom: 16 }}>
        <div>Location</div>
        <Select
          name="states"
          value={state || { value: 0, label: '' }}
          options={locationList.map(l => ({
            label: l.label,
            value: l.value,
          }))}
          onChange={e => {
            setState(e);
          }}
          styles={customStyles}
          components={{
            IndicatorSeparator: null,
          }}
        />
        {state.label && (
          <>
            <Municipality
              changePropertyInfo={changePropertyInfo}
              property={property}
              setPropertyInfo={setProperty}
              state={state}
            />
            <PropertySection
              changePropertyInfo={changePropertyInfo}
              property={property}
              findMatches={findMatches}
              isLoading={isLoading}
              callRpadValidation={callRpadValidation}
              setProperty={setProperty}
              setTypeAheadMatches={setTypeAheadMatches}
              typeAheadMatches={typeAheadMatches}
              setAddressResultClicked={setAddressResultClicked}
              state={state}
            />
            <div style={{ display: 'flex', marginTop: 30, justifyContent: 'space-between' }}>
              <Button
                buttonText="Clear All"
                width="308px"
                buttonType="ButtonSecondaryMedium"
                onClick={onClear}
              />
              <Button
                buttonText={addressResultClicked ? 'Go' : 'Search'}
                width="308px"
                buttonType="ButtonPrimaryMedium"
                onClick={() => {
                  addressResultClicked
                    ? history.push(`/property-info/${property.rpadId}/${state.label}`)
                    : onSearch();
                }}
              />
            </div>
          </>
        )}
      </div>
      {matches && (
        <div style={{ borderTop: '1px solid #e5e5ea' }}>
          <Header>
            <div>Street Address</div>
            <div>Municipality</div>
            <div>Muni Code</div>
            {!isNj && <div>Section</div>}
            <div>Block</div>
            <div>Lot</div>
            {isNj && <div>Qualifier</div>}
            <div>Owner Name</div>
          </Header>
        </div>
      )}
      {resultsLoading && (
        <span style={{ position: 'absolute', top: '80%', right: '50%' }}>
          <Loader active={resultsLoading} size="large" />
        </span>
      )}
      {matches &&
        matches?.results.map((r, i) => {
          return (
            <PropertyResults
              propertyResult={r}
              isNj={isNj}
              onPropertyClick={() => {
                history.push(`/property-info/${r.rpadId}/${state.label}`);
              }}
            />
          );
        })}
    </>
  );
};

export default AddressInfo;
